<template>
    <div class="flex flex-col w-[100dvw] h-[100dvh] bg-gray-100 justify-between" id="mainContainer">
        <div class="flex flex-row justify-center bg-gray-300" id="logoContainer">
            <div class="flex items-center gap-3 px-8 py-6">
                <img :src="logo" class="h-[2.3rem] cursor-pointer" />
            </div>
        </div>

        <div v-if="loading" class="flex flex-col items-center w-full pt-6 h-full">
            <div class="w-[100vw] md:w-auto md:h-[55vh] aspect-video mb-2">
                <Skeleton width="100%" height="100%"></Skeleton>
            </div>
        </div>

        <div v-else-if="notFound" class="flex flex-col items-center h-full">
            <NotFound></NotFound>
        </div>

        <div v-else class="flex flex-col items-center pt-6 h-full">
            <div class="w-[100vw] md:w-[60vw] md:rounded-md aspect-video md:border-2 md:border-white shadow-lg">
                <div v-if="videoUrl" class="w-full h-full flex flex-col relative">
                    <video
                        ref="videoPlayer"
                        class="h-full sm:rounded-none md:rounded-none md:rounded-t-md aspect-video"
                        :controls="!showPlay"
                    >
                        <source :src="videoUrl" />
                    </video>

                    <div
                        v-if="testimonial.products.length"
                        id="productBoxLg"
                        class="hidden md:flex flex-row border-2 p-2 border-t-white border-x-0 border-b-0 gap-2"
                    >
                        <p
                            v-for="(item, index) in testimonial.products"
                            :key="index"
                            class="md:text-xl text-md font-bold"
                        >
                            {{ item.name }} {{ index == testimonial.products.length - 1 ? "" : "|" }}
                        </p>
                    </div>

                    <Button
                        id="playButton"
                        unstyled
                        v-if="showPlay"
                        class="center-absolute bg-gray-300 border-0 rounded-full w-[6rem] h-[6rem]"
                        aria-label="Filter"
                        @click="play"
                    >
                        <img :src="playButton" class="pl-1 w-[2.5rem] center-absolute" />
                    </Button>
                </div>
            </div>
            <div
                v-if="testimonial.products.length"
                id="productBox"
                class="md:hidden md:text-2xl text-md w-full text-center font-bold p-2 shadow-[0_5px_4px_-1px_rgba(0,0,0,0.1)]"
            >
                <p v-for="(item, index) in testimonial.products" :key="index" class="md:text-xl text-md font-bold">
                    {{ item.name }}
                </p>
            </div>
        </div>

        <div class="flex flex-col items-center gap-8 pb-20">
            <div class="flex flex-row gap-6">
                <a
                    v-for="(item, index) in links"
                    :key="index"
                    :href="item.link"
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    <div
                        :class="['pi', item.icon, 'p-2 bg-black rounded-4xl text-2xl cursor-pointer text-white']"
                    ></div>
                </a>
            </div>

            <p class="text-[8px]">© Cardone Training Technologies Inc. All Rights Reserved</p>
        </div>
    </div>
</template>

<script setup>
import logo from "@/assets/login_logo.png";
import playButton from "@/assets/play_button.svg";
import { onMounted, ref, inject } from "vue";
import { useRoute } from "vue-router";
import Skeleton from "primevue/skeleton";
import { Button } from "primevue";
import NotFound from "../NotFound.vue";

const shareService = inject("shareService");

const router = useRoute();

const loading = ref(true);
const notFound = ref(false);
const videoUrl = ref();
const testimonial = ref();
const videoPlayer = ref(null);
const showPlay = ref(true);

const links = ref([
    {
        icon: "pi-facebook",
        link: "https://www.facebook.com/grantcardonefan",
    },
    {
        icon: "pi-twitter",
        link: "https://x.com/grantcardone",
    },
    {
        icon: "pi-instagram",
        link: "https://www.instagram.com/grantcardone",
    },
    {
        icon: "pi-youtube",
        link: "https://www.youtube.com/@GrantCardone/videos",
    },
    {
        icon: "pi-tiktok",
        link: "https://www.tiktok.com/@grantcardone",
    },
]);

onMounted(async () => {
    const id = router.params.shareId;

    try {
        const share = await shareService.getById(id);

        videoUrl.value = `${process.env.VUE_APP_API_URL}/v1/share/${id}/video`;
        testimonial.value = share;
    } catch (error) {
        notFound.value = true;
    } finally {
        loading.value = false;
    }
});

const play = () => {
    videoPlayer.value.play();
    showPlay.value = false;
};
</script>

<style scoped>
.center-absolute {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
}

#logoContainer {
    background-color: rgba(209, 213, 220, 0.4);
}

#playButton {
    background-color: rgba(209, 213, 220, 0.8);
}

#productBox {
    background-color: rgba(209, 213, 220, 0.2);
}

#productBoxLg {
    background-color: rgba(209, 213, 220, 0.15);
}

#mainContainer {
    background-image: url("~@/assets/playerBackground.png");
    background-repeat: no-repeat;
    background-size: cover;
}
</style>
